/*
 * assets/theme.js — Boostly core runtime
 * Replaces the original vendor.js (jQuery/lodash/slick bundle), theme.js (Debut/Slate),
 * ajaxify-cart.liquid helpers, currencies helpers and the Bootstrap modal runtime.
 * Exposes window.Theme (CONVENTIONS §3) + core custom elements:
 *   <boostly-cart-count>, <boostly-quantity>, <boostly-modal>, <boostly-tabs>, <boostly-accordion>
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------- config */
  let config = {};
  try {
    const el = document.getElementById('ThemeConfigJson');
    if (el) config = JSON.parse(el.textContent);
  } catch (e) { console.warn('[Boostly] invalid ThemeConfigJson', e); }

  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  /* ---------------------------------------------------------------- events */
  const events = {
    on(name, fn, target = document) { target.addEventListener(name, fn); return () => target.removeEventListener(name, fn); },
    off(name, fn, target = document) { target.removeEventListener(name, fn); },
    emit(name, detail, target = document) { target.dispatchEvent(new CustomEvent(name, { detail, bubbles: true })); }
  };

  /* ----------------------------------------------------------------- utils */
  function debounce(fn, wait = 300) {
    let t;
    return function (...args) { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); };
  }
  function throttle(fn, wait = 100) {
    let last = 0, t;
    return function (...args) {
      const now = Date.now(), remaining = wait - (now - last);
      if (remaining <= 0) { last = now; fn.apply(this, args); }
      else { clearTimeout(t); t = setTimeout(() => { last = Date.now(); fn.apply(this, args); }, remaining); }
    };
  }
  const random = {
    int(min, max) { min = Math.ceil(min); max = Math.floor(max); return Math.floor(Math.random() * (max - min + 1)) + min; },
    pick(arr) { return arr[Math.floor(Math.random() * arr.length)]; },
    shuffle(arr) { const a = arr.slice(); for (let i = a.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [a[i], a[j]] = [a[j], a[i]]; } return a; }
  };
  function interpolate(str, vars) {
    return String(str || '').replace(/\[(\w+)\]|\{\{\s*(\w+)\s*\}\}/g, (m, a, b) => {
      const k = a || b; return vars[k] !== undefined ? vars[k] : m;
    });
  }
  function htmlToElement(html) {
    const t = document.createElement('template'); t.innerHTML = html.trim(); return t.content.firstElementChild;
  }

  /* ----------------------------------------------------------------- money */
  // Port of Shopify.formatMoney — supports every Shopify money placeholder.
  const money = {
    format(cents, format) {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      cents = Number(cents) || 0;
      format = format || config.moneyFormat || '${{amount}}';
      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      function withDelims(number, precision, thousands = ',', decimal = '.') {
        if (isNaN(number) || number == null) return '0';
        number = (number / 100).toFixed(precision);
        const parts = number.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        const c = parts[1] ? decimal + parts[1] : '';
        return dollars + c;
      }
      const match = format.match(placeholder);
      if (!match) return format;
      let value;
      switch (match[1]) {
        case 'amount': value = withDelims(cents, 2); break;
        case 'amount_no_decimals': value = withDelims(cents, 0); break;
        case 'amount_with_comma_separator': value = withDelims(cents, 2, '.', ','); break;
        case 'amount_no_decimals_with_comma_separator': value = withDelims(cents, 0, '.', ','); break;
        case 'amount_with_apostrophe_separator': value = withDelims(cents, 2, "'", '.'); break;
        case 'amount_no_decimals_with_space_separator': value = withDelims(cents, 0, ' ', ''); break;
        case 'amount_with_space_separator': value = withDelims(cents, 2, ' ', ','); break;
        case 'amount_with_period_and_space_separator': value = withDelims(cents, 2, ' ', '.'); break;
        default: value = withDelims(cents, 2);
      }
      return format.replace(placeholder, value);
    },
    stripHtml(s) { const d = document.createElement('div'); d.innerHTML = s; return d.textContent; }
  };

  /* --------------------------------------------------------------- cookies */
  const cookies = {
    get(name) {
      const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    },
    set(name, value, opts = {}) {
      let str = `${name}=${encodeURIComponent(value)}; path=${opts.path || '/'}; SameSite=Lax`;
      if (opts.expires !== undefined) {
        const d = opts.expires instanceof Date ? opts.expires : new Date(Date.now() + Number(opts.expires) * 1000);
        str += '; expires=' + d.toUTCString();
      }
      document.cookie = str;
    },
    remove(name, path = '/') { document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`; }
  };

  /* ------------------------------------------------------------------ cart */
  const routes = Object.assign({
    root: '/', cart: '/cart', cartAdd: '/cart/add', cartChange: '/cart/change', cartUpdate: '/cart/update', cart_js: '/cart.js'
  }, config.routes || {});

  async function request(url, body) {
    const opts = { headers: { Accept: 'application/json' }, credentials: 'same-origin' };
    if (body !== undefined) {
      opts.method = 'POST';
      if (body instanceof FormData) { opts.body = body; opts.headers['X-Requested-With'] = 'XMLHttpRequest'; }
      else { opts.body = JSON.stringify(body); opts.headers['Content-Type'] = 'application/json'; }
    }
    const res = await fetch(url, opts);
    let json = null;
    try { json = await res.json(); } catch (e) { /* ignore */ }
    if (!res.ok || (json && json.status && Number(json.status) >= 400)) {
      const err = new Error((json && (json.description || json.message)) || res.statusText);
      err.status = json && json.status || res.status; err.response = json;
      throw err;
    }
    return json;
  }

  const cart = {
    state: null,
    async get() { const c = await request(routes.cart_js); cart.state = c; return c; },
    async add(items, { sections } = {}) {
      let body;
      if (items instanceof FormData) { body = items; if (sections) body.append('sections', sections.join(',')); }
      else {
        const list = Array.isArray(items) ? items : [items];
        body = { items: list.map(i => ({ id: Number(i.id), quantity: Number(i.quantity || 1), properties: i.properties || undefined, selling_plan: i.selling_plan || undefined })) };
        if (sections) body.sections = sections.join(',');
      }
      const result = await request(routes.cartAdd + '.js', body);
      const fresh = await cart.get();
      events.emit('cart:added', { cart: fresh, items: result.items || [result], sections: result.sections });
      events.emit('cart:updated', fresh);
      return result;
    },
    addWithSections(formData, sections) { return cart.add(formData, { sections }); },
    async change(lineOrKey, quantity, { sections, properties } = {}) {
      const body = { quantity: Number(quantity) };
      if (typeof lineOrKey === 'number' || /^\d+$/.test(String(lineOrKey))) body.line = Number(lineOrKey); else body.id = lineOrKey;
      if (properties) body.properties = properties;
      if (sections) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }
      const result = await request(routes.cartChange + '.js', body);
      cart.state = result;
      events.emit('cart:updated', result);
      return result;
    },
    async update(updates, { sections, note, attributes } = {}) {
      const body = {};
      if (updates) body.updates = updates;
      if (note !== undefined) body.note = note;
      if (attributes) body.attributes = attributes;
      if (sections) { body.sections = sections.join(','); body.sections_url = window.location.pathname; }
      const result = await request(routes.cartUpdate + '.js', body);
      const fresh = await cart.get();
      events.emit('cart:updated', fresh);
      return Object.assign({}, fresh, { sections: result.sections });
    },
    async sections(ids) {
      const url = `${window.location.pathname}?sections=${ids.join(',')}`;
      return request(url);
    }
  };

  /* ------------------------------------------------------------------ a11y */
  const FOCUSABLE = 'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), iframe, summary';
  const a11y = {
    _handlers: null,
    getFocusable(el) { return Array.from(el.querySelectorAll(FOCUSABLE)).filter(n => n.offsetParent !== null || n === document.activeElement); },
    trapFocus(container, { elementToFocus } = {}) {
      a11y.removeTrapFocus();
      const els = a11y.getFocusable(container);
      const first = els[0], last = els[els.length - 1];
      const onKey = (e) => {
        if (e.key !== 'Tab') return;
        const list = a11y.getFocusable(container); const f = list[0], l = list[list.length - 1];
        if (!f) { e.preventDefault(); return; }
        if (e.shiftKey && document.activeElement === f) { e.preventDefault(); l.focus(); }
        else if (!e.shiftKey && document.activeElement === l) { e.preventDefault(); f.focus(); }
      };
      document.addEventListener('keydown', onKey);
      a11y._handlers = onKey;
      (elementToFocus || first || container).focus({ preventScroll: true });
      return last;
    },
    removeTrapFocus(elementToFocus) {
      if (a11y._handlers) document.removeEventListener('keydown', a11y._handlers);
      a11y._handlers = null;
      if (elementToFocus) elementToFocus.focus({ preventScroll: true });
    }
  };

  /* ----------------------------------------------------------------- modal */
  // Works on <dialog class="modal"> and on [data-modal] containers.
  let lastTrigger = null;
  const modal = {
    resolve(target) { return typeof target === 'string' ? document.getElementById(target) || document.querySelector(`[data-modal="${target}"]`) : target; },
    open(target, trigger) {
      const el = modal.resolve(target); if (!el) return;
      lastTrigger = trigger || document.activeElement;
      if (el.tagName === 'DIALOG') { if (!el.open) el.showModal(); }
      else { el.hidden = false; el.setAttribute('open', ''); el.setAttribute('aria-hidden', 'false'); }
      document.body.classList.add('modal-open');
      requestAnimationFrame(() => el.classList.add('is-open'));
      a11y.trapFocus(el, { elementToFocus: el.querySelector('[autofocus]') || undefined });
      events.emit('modal:open', { id: el.id }, el);
    },
    close(target) {
      const el = modal.resolve(target); if (!el) return;
      el.classList.remove('is-open');
      if (el.tagName === 'DIALOG') { if (el.open) el.close(); }
      else { el.hidden = true; el.removeAttribute('open'); el.setAttribute('aria-hidden', 'true'); }
      if (!document.querySelector('dialog[open], [data-modal][open]')) document.body.classList.remove('modal-open');
      a11y.removeTrapFocus(lastTrigger);
      events.emit('modal:close', { id: el.id }, el);
    }
  };
  // Global wiring: [data-modal-open="id"], [data-modal-close], backdrop click, ESC.
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-modal-open]');
    if (opener) { e.preventDefault(); modal.open(opener.getAttribute('data-modal-open'), opener); return; }
    const closer = e.target.closest('[data-modal-close]');
    if (closer) { e.preventDefault(); modal.close(closer.closest('dialog, [data-modal]')); return; }
    // Click directly on a <dialog class="modal"> (i.e. the backdrop area outside .modal__content) closes it.
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('modal') && !e.target.hasAttribute('data-no-backdrop-close')) {
      modal.close(e.target); return;
    }
    if (e.target.matches('[data-modal][open]')) modal.close(e.target);
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.tagName === 'DIALOG') { e.preventDefault(); modal.close(e.target); }
  }, true);
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('[data-modal][open]');
    if (open) modal.close(open);
  });

  /* ---------------------------------------------------------------- drawer */
  const drawer = {
    get(id) { return document.querySelector(`[data-drawer="${id}"]`); },
    open(id, trigger) {
      const el = drawer.get(id); if (!el) return;
      lastTrigger = trigger || document.activeElement;
      el.hidden = false;
      requestAnimationFrame(() => { el.classList.add('is-open'); el.setAttribute('aria-hidden', 'false'); });
      document.body.classList.add('drawer-open');
      document.querySelectorAll(`[aria-controls="${el.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'true'));
      a11y.trapFocus(el, { elementToFocus: el.querySelector('[autofocus], input[type="search"]') || undefined });
      events.emit('drawer:open', { id }, el);
    },
    close(id) {
      const el = typeof id === 'string' ? drawer.get(id) : id; if (!el) return;
      el.classList.remove('is-open'); el.setAttribute('aria-hidden', 'true');
      document.querySelectorAll(`[aria-controls="${el.id}"]`).forEach(b => b.setAttribute('aria-expanded', 'false'));
      setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, 300);
      if (!document.querySelector('[data-drawer].is-open')) document.body.classList.remove('drawer-open');
      a11y.removeTrapFocus(lastTrigger);
      events.emit('drawer:close', { id: el.getAttribute('data-drawer') }, el);
    },
    toggle(id, trigger) { const el = drawer.get(id); if (el && el.classList.contains('is-open')) drawer.close(id); else drawer.open(id, trigger); }
  };
  document.addEventListener('click', (e) => {
    const t = e.target.closest('[data-drawer-toggle]');
    if (t) { e.preventDefault(); drawer.toggle(t.getAttribute('data-drawer-toggle'), t); return; }
    const c = e.target.closest('[data-drawer-close]');
    if (c) { e.preventDefault(); drawer.close(c.closest('[data-drawer]')); return; }
    if (e.target.matches('[data-drawer-overlay]')) drawer.close(e.target.closest('[data-drawer]'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { const open = document.querySelector('[data-drawer].is-open'); if (open) drawer.close(open); }
  });

  /* ----------------------------------------------------------------- toast */
  const toast = {
    show(html, { type = 'success', timeout = 4000 } = {}) {
      let wrap = document.getElementById('ToastContainer');
      if (!wrap) { wrap = document.createElement('div'); wrap.id = 'ToastContainer'; wrap.className = 'toast-container'; document.body.appendChild(wrap); }
      const el = document.createElement('div');
      el.className = `toast toast--${type}`;
      el.setAttribute('role', type === 'error' ? 'alert' : 'status');
      el.innerHTML = `<div class="toast__body">${html}</div><button type="button" class="toast__close" aria-label="${(config.strings && config.strings.close) || 'Close'}">&times;</button>`;
      el.querySelector('.toast__close').addEventListener('click', () => dismiss());
      wrap.appendChild(el);
      requestAnimationFrame(() => el.classList.add('is-visible'));
      let timer = timeout ? setTimeout(dismiss, timeout) : null;
      el.addEventListener('mouseenter', () => clearTimeout(timer));
      el.addEventListener('mouseleave', () => { if (timeout) timer = setTimeout(dismiss, 1500); });
      function dismiss() { el.classList.remove('is-visible'); setTimeout(() => el.remove(), 300); }
      return { dismiss, el };
    }
  };

  /* --------------------------------------------------------- section helpers */
  async function fetchSection(sectionId, url = window.location.href) {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('section_id', sectionId);
    const res = await fetch(u.toString(), { credentials: 'same-origin' });
    const text = await res.text();
    return new DOMParser().parseFromString(text, 'text/html');
  }

  window.Theme = Object.assign(window.Theme || {}, {
    config,
    strings: config.strings || {},
    settings: config.settings || {},
    moneyFormat: config.moneyFormat || '${{amount}}',
    routes, money, cart, cookies, a11y, modal, drawer, toast, events, debounce, throttle, random,
    interpolate, htmlToElement, fetchSection,
    isMobile() { return window.matchMedia('(max-width: 749px)').matches; },
    prefersReducedMotion() { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
  });

  /* ======================================================= custom elements */
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* <boostly-cart-count> — updates count / total / singular-plural label on cart:updated */
  class CartCount extends HTMLElement {
    connectedCallback() {
      this._off = events.on('cart:updated', (e) => this.render(e.detail));
    }
    disconnectedCallback() { if (this._off) this._off(); }
    render(c) {
      if (!c) return;
      this.querySelectorAll('.cart-count__value').forEach(el => { el.textContent = c.item_count; });
      this.querySelectorAll('.cart-count__total').forEach(el => { el.textContent = money.format(c.total_price); });
      this.querySelectorAll('.cart-count__label').forEach(el => {
        el.textContent = c.item_count === 1 ? (config.strings.itemsOne || 'item') : (config.strings.itemsOther || 'items');
      });
      this.classList.toggle('is-empty', c.item_count === 0);
    }
  }
  define('boostly-cart-count', CartCount);

  /* <boostly-quantity> — input + −/+ buttons, respects min/max/step, emits change */
  class Quantity extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-qty]'); if (!btn) return;
        e.preventDefault();
        const step = Number(this.input.step) || 1;
        const min = this.input.min !== '' ? Number(this.input.min) : 1;
        const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
        let v = Number(this.input.value) || min;
        v = btn.getAttribute('data-qty') === 'plus' ? v + step : v - step;
        v = Math.min(Math.max(v, min), max);
        if (String(v) === this.input.value) return;
        this.input.value = v;
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }
    updateButtons() {
      const v = Number(this.input.value);
      const min = this.input.min !== '' ? Number(this.input.min) : 1;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('[data-qty="minus"]'); const plus = this.querySelector('[data-qty="plus"]');
      if (minus) minus.disabled = v <= min;
      if (plus) plus.disabled = v >= max;
    }
    get value() { return Number(this.input && this.input.value) || 1; }
    set value(v) { if (this.input) { this.input.value = v; this.updateButtons(); } }
  }
  define('boostly-quantity', Quantity);

  /* <boostly-modal> — thin wrapper: <boostly-modal id="x"><dialog class="modal">…</dialog></boostly-modal> */
  class ModalEl extends HTMLElement {
    get dialog() { return this.querySelector('dialog, [data-modal]'); }
    open(trigger) { modal.open(this.dialog, trigger); }
    close() { modal.close(this.dialog); }
  }
  define('boostly-modal', ModalEl);

  /* <boostly-tabs> — [role=tab][aria-controls] + [role=tabpanel] */
  class Tabs extends HTMLElement {
    connectedCallback() {
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i));
        tab.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
            e.preventDefault();
            const n = (i + (e.key === 'ArrowRight' ? 1 : -1) + this.tabs.length) % this.tabs.length;
            this.select(n); this.tabs[n].focus();
          }
        });
      });
    }
    select(index) {
      this.tabs.forEach((t, i) => {
        const on = i === index;
        t.setAttribute('aria-selected', on); t.tabIndex = on ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = !on;
      });
    }
  }
  define('boostly-tabs', Tabs);

  /* <boostly-accordion data-single> — <details> based; data-single closes siblings */
  class Accordion extends HTMLElement {
    connectedCallback() {
      this.addEventListener('toggle', (e) => {
        if (!this.hasAttribute('data-single')) return;
        const d = e.target; if (d.tagName !== 'DETAILS' || !d.open) return;
        this.querySelectorAll('details[open]').forEach(o => { if (o !== d) o.open = false; });
      }, true);
    }
  }
  define('boostly-accordion', Accordion);

  /* Cart count bootstrap: refresh once when page restored from bfcache */
  window.addEventListener('pageshow', (e) => { if (e.persisted) cart.get().then(c => events.emit('cart:updated', c)).catch(() => {}); });

  /* Theme editor: re-dispatch section events for custom elements */
  if (config.settings && config.settings.designMode) {
    ['shopify:section:load', 'shopify:section:unload', 'shopify:section:select', 'shopify:section:deselect', 'shopify:block:select', 'shopify:block:deselect']
      .forEach(n => document.addEventListener(n, (e) => events.emit('theme:' + n.split(':').slice(1).join(':'), e.detail, e.target)));
  }
})();
